/**
 * `useCaseFilesOpenStore` — which Acervo nodes are expanded, per case, across
 * sessions (operator 2026-08-01).
 *
 * Three things were asked for together and they interlock:
 *   1. expand-all / collapse-all controls on the ACERVO root;
 *   2. the state SURVIVES leaving the case and coming back;
 *   3. the initial state is **all groups collapsed**.
 *
 * ## Collapsed is the DEFAULT, so an EXPANDED group is the deviation
 *
 * Before this, expansion was `useState` and every group rendered OPEN — so a
 * case with six modality groups opened as a wall of rows the lawyer had to fold
 * by hand, every time. Collapsed-by-default makes the tier a table of contents
 * you drill into, which is the same reasoning that put the file rows on
 * expand-on-select.
 *
 * The ROOT is the exception: it defaults OPEN, because a collapsed root hides
 * the entire tier including the controls that would reopen its children. Two
 * different defaults is why this stores a resolved boolean per node and asks
 * `defaultOpenFor` what the deviation is, rather than storing "collapsed keys"
 * and inferring — an inference that is right for groups and wrong for the root.
 *
 * ## Storage: mirrors `useCaseFilesSortStore` deliberately
 *
 * Same case-scoped `${caseKey}:${nodeKey}` shape, same localStorage +
 * `partialize` allowlist, same deviations-only write with an MRU-evicted cap.
 * These two stores describe the same tree and a lawyer changes them in the same
 * gesture, so a future reader should find one shape, not two. The cap matters
 * for the same reason: case-scoping multiplies the key space, and a case never
 * reopened must not hold storage forever.
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { createTenantScopedStorage } from "@/lib/tenant-scoped-storage";

/** The ACERVO tier root. Not a group key — no modality can collide with it,
 *  because group keys come from the server's `UploadRoute.modality` set. */
export const ROOT_NODE_KEY = "__root__";

/** Bounded record size; oldest WRITE evicted first. */
export const OPEN_CAP = 400;

/** Storage key for one node within one case scope. */
export function openKeyFor(caseKey: string, nodeKey: string): string {
  return `${caseKey}:${nodeKey}`;
}

/**
 * The default for a node when nothing is stored.
 *
 * **Root OPEN, groups COLLAPSED** — see the docblock. Keep this the single
 * place that knows the asymmetry: a caller that hard-codes `false` will get the
 * root wrong, and a collapsed root hides its own reopen control.
 */
export function defaultOpenFor(nodeKey: string): boolean {
  return nodeKey === ROOT_NODE_KEY;
}

/**
 * Deviations-only write with MRU eviction, matching `putSort`.
 *
 * A node at its default is REMOVED rather than stored `false`, so collapsing
 * everything (the common state) shrinks the record back to nothing instead of
 * writing a row per group per case.
 *
 * Pure — exported for unit test.
 */
export function putOpen(
  record: Readonly<Record<string, boolean>>,
  caseKey: string,
  nodeKey: string,
  next: boolean,
  cap = OPEN_CAP,
): Record<string, boolean> {
  const key = openKeyFor(caseKey, nodeKey);
  const out: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(record)) if (k !== key) out[k] = v;
  if (next !== defaultOpenFor(nodeKey)) out[key] = next;
  const entries = Object.entries(out);
  if (entries.length <= cap) return out;
  return Object.fromEntries(entries.slice(entries.length - cap));
}

/**
 * Set MANY nodes at once — the expand-all / collapse-all controls.
 *
 * Applies the same deviations-only rule per node, so "collapse all" (every
 * group at its default) writes NOTHING and clears any existing rows for that
 * case, while "expand all" writes one row per group. The root is deliberately
 * NOT included by the caller: expanding the tier's children while its root is
 * shut would be invisible, and collapsing the root is a separate gesture the
 * lawyer already has.
 *
 * Pure — exported for unit test.
 */
export function putManyOpen(
  record: Readonly<Record<string, boolean>>,
  caseKey: string,
  nodeKeys: readonly string[],
  next: boolean,
  cap = OPEN_CAP,
): Record<string, boolean> {
  let out: Record<string, boolean> = { ...record };
  for (const nodeKey of nodeKeys) out = putOpen(out, caseKey, nodeKey, next, cap);
  return out;
}

interface CaseFilesOpenStore {
  byNode: Record<string, boolean>;
  /** Resolved open state — the node's default when nothing is stored. */
  isOpen: (caseKey: string, nodeKey: string) => boolean;
  toggle: (caseKey: string, nodeKey: string) => void;
  /** Expand-all / collapse-all over the groups the view is showing. */
  setMany: (caseKey: string, nodeKeys: readonly string[], open: boolean) => void;
  clear: () => void;
}

export const useCaseFilesOpenStore = create<CaseFilesOpenStore>()(
  persist(
    (set, get) => ({
      byNode: {},
      isOpen: (caseKey, nodeKey) =>
        get().byNode[openKeyFor(caseKey, nodeKey)] ?? defaultOpenFor(nodeKey),
      toggle: (caseKey, nodeKey) => {
        const current =
          get().byNode[openKeyFor(caseKey, nodeKey)] ?? defaultOpenFor(nodeKey);
        set((s) => ({ byNode: putOpen(s.byNode, caseKey, nodeKey, !current) }));
      },
      setMany: (caseKey, nodeKeys, open) =>
        set((s) => ({ byNode: putManyOpen(s.byNode, caseKey, nodeKeys, open) })),
      clear: () => set({ byNode: {} }),
    }),
    {
      name: "lexide:case-files-open",
      storage: createJSONStorage(() => createTenantScopedStorage(() => localStorage)),
      partialize: (state) => ({ byNode: state.byNode }),
    },
  ),
);
